import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Star, CheckCircle, XCircle, ChevronDown, Plus, X, Search } from 'lucide-react'
import { reviewsService } from '@/services/reviewsService'
import { productsService } from '@/services/productsService'
import { usePagination } from '@/hooks/usePagination'
import { useDebounce } from '@/hooks/useDebounce'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import { formatRelativeTime } from '@/utils/formatters'

function StarRating({ rating }) {
  return (
    <div style={{ display:'flex', gap:2 }}>
      {[1,2,3,4,5].map((n) => (
        <Star key={n} size={14} style={{ color: n <= rating ? '#FF6D00' : '#E8E8E8', fill: n <= rating ? '#FF6D00' : 'none' }} />
      ))}
    </div>
  )
}

export default function ReviewsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()

  const [activeTab, setActiveTab] = useState('pending')
  const [addOpen, setAddOpen] = useState(false)
  const { page, limit, setPage } = usePagination(1, 15)

  const STATUS_LABELS = { pending:t('vendors.pendingReview'), approved:t('common.approved'), rejected:t('common.rejected') }
  const STATUS_TABS = [
    { key:'pending',  label:t('vendors.pendingReview'), color:'#FF6D00' },
    { key:'approved', label:t('common.approved'),       color:'#1B6D24' },
    { key:'rejected', label:t('common.rejected'),       color:'#BA1A1A' },
  ]

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reviews', { status:activeTab, page, limit }],
    queryFn: () => reviewsService.getAll({ status:activeTab, page, limit }),
  })

  // Pending count badge
  const { data: pendingData } = useQuery({
    queryKey: ['reviews-pending-count'],
    queryFn: () => reviewsService.getAll({ status:'pending', limit:1 }),
    refetchInterval: 60_000,
  })
  const pendingCount = pendingData?.meta?.total || 0

  const reviews = data?.data || []
  const meta    = data?.meta || {}

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['reviews'] })
    qc.invalidateQueries({ queryKey: ['reviews-pending-count'] })
  }

  const approveMut = useMutation({
    mutationFn: (id) => reviewsService.approve(id),
    onSuccess: () => { toast.success(t('reviews.approveSuccess')); invalidate() },
    onError: () => toast.error(t('common.failed')),
  })

  const rejectMut = useMutation({
    mutationFn: (id) => reviewsService.reject(id),
    onSuccess: () => { toast.success(t('reviews.rejectSuccess')); invalidate() },
    onError: () => toast.error(t('common.failed')),
  })

  const createMut = useMutation({
    mutationFn: (payload) => reviewsService.create(payload),
    onSuccess: () => {
      toast.success(t('reviews.addSuccess'))
      setAddOpen(false)
      setActiveTab('approved')
      setPage(1)
      invalidate()
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('reviews.addError')),
  })

  return (
    <div className="space-y-5">
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 className="font-black text-navy text-xl" style={{ fontFamily:'Cairo, sans-serif' }}>{t('reviews.title')}</h1>
          <p className="text-sm text-ghost mt-1" style={{ fontFamily:'Tajawal, sans-serif' }}>{t('reviews.pageSubtitle')}</p>
        </div>
        <button onClick={() => setAddOpen(true)} className="hg-btn-navy px-5 py-2 text-sm"
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={16} />{t('reviews.addReview')}
        </button>
      </div>

      {/* Status tabs */}
      <div className="hg-card p-1 flex gap-1">
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key
          const showBadge = tab.key === 'pending' && pendingCount > 0
          return (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1) }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 20px', borderRadius:12, fontSize:13, fontWeight:700, fontFamily:'Cairo, sans-serif', border:'none', cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.2s',
                background: isActive ? tab.color : 'transparent',
                color: isActive ? '#fff' : '#767683',
                boxShadow: isActive ? `0 4px 12px ${tab.color}30` : 'none' }}>
              {tab.label}
              {showBadge && <span style={{ background:'#FF6D00', color:'#fff', fontSize:11, fontWeight:700, padding:'1px 7px', borderRadius:20 }}>{pendingCount}</span>}
            </button>
          )
        })}
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="hg-card p-8 flex flex-col items-center gap-4">
          <p style={{ color:'#BA1A1A', fontWeight:700, fontFamily:'Cairo, sans-serif' }}>{t('common.loadFailed')}</p>
          <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">{t('common.retry')}</button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="hg-card"><EmptyState icon={Star} message={t('reviews.emptyMessage', { status: STATUS_LABELS[activeTab] })} /></div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(360px, 1fr))', gap:16 }}>
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} lang={lang} t={t}
                onApprove={() => approveMut.mutate(review.id)}
                onReject={() => rejectMut.mutate(review.id)}
                approveLoading={approveMut.isPending}
                rejectLoading={rejectMut.isPending}
              />
            ))}
          </div>
          <div className="hg-card p-4">
            <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
          </div>
        </>
      )}

      {addOpen && (
        <AddReviewDialog
          lang={lang}
          t={t}
          onClose={() => setAddOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
          submitting={createMut.isPending}
        />
      )}
    </div>
  )
}

function AddReviewDialog({ lang, t, onClose, onSubmit, submitting }) {
  const [product, setProduct] = useState(null)
  const [productSearch, setProductSearch] = useState('')
  const debouncedSearch = useDebounce(productSearch, 350)
  const [authorName, setAuthorName] = useState('')
  const [rating, setRating] = useState(5)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')

  const { data: productsData, isFetching } = useQuery({
    queryKey: ['reviews-product-search', debouncedSearch],
    queryFn: () => productsService.getAll({ search: debouncedSearch, limit: 8 }),
    enabled: !product && debouncedSearch.trim().length >= 2,
  })
  const results = productsData?.data || []

  const productName = (p) => (typeof p?.name === 'object' ? (p.name[lang] || p.name.en) : p?.name) || '—'

  const canSubmit = product && authorName.trim().length >= 2 && rating >= 1 && !submitting

  const submit = () => {
    if (!canSubmit) return
    onSubmit({
      productId: product.id,
      authorName: authorName.trim(),
      rating,
      title: title.trim() || undefined,
      content: content.trim() || undefined,
    })
  }

  const inputStyle = { width:'100%', padding:'10px 12px', borderRadius:10, border:'1px solid #E8E8E8', fontSize:13, fontFamily:'Tajawal, sans-serif', outline:'none' }
  const labelStyle = { display:'block', fontSize:12, fontWeight:700, color:'#454652', fontFamily:'Cairo, sans-serif', marginBottom:6 }

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div onClick={(e) => e.stopPropagation()} dir="rtl" style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:460, maxHeight:'90vh', overflowY:'auto', padding:22 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:16, color:'#000666' }}>{t('reviews.addDialogTitle')}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#767683' }}><X size={20} /></button>
        </div>

        {/* Product */}
        <div style={{ marginBottom:14 }}>
          <label style={labelStyle}>{t('reviews.product')}</label>
          {product ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'10px 12px', borderRadius:10, background:'rgba(0,6,102,0.06)' }}>
              <span style={{ fontSize:13, fontWeight:700, fontFamily:'Cairo, sans-serif', color:'#000666' }}>{productName(product)}</span>
              <button onClick={() => { setProduct(null); setProductSearch('') }} style={{ background:'none', border:'none', cursor:'pointer', color:'#BA1A1A', fontSize:12, fontFamily:'Cairo, sans-serif', fontWeight:700 }}>{t('reviews.changeBtn')}</button>
            </div>
          ) : (
            <div style={{ position:'relative' }}>
              <div style={{ position:'relative' }}>
                <Search size={15} style={{ position:'absolute', top:11, insetInlineStart:12, color:'#B0B0B8' }} />
                <input autoFocus value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={t('reviews.productSearchPlaceholder')} style={{ ...inputStyle, paddingInlineStart:34 }} />
              </div>
              {debouncedSearch.trim().length >= 2 && (
                <div style={{ marginTop:6, border:'1px solid #F0F0F0', borderRadius:10, maxHeight:180, overflowY:'auto' }}>
                  {isFetching ? (
                    <div style={{ padding:12, fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>{t('reviews.searching')}</div>
                  ) : results.length === 0 ? (
                    <div style={{ padding:12, fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>{t('reviews.noResults')}</div>
                  ) : results.map((p) => (
                    <button key={p.id} onClick={() => setProduct(p)}
                      style={{ display:'block', width:'100%', textAlign:'start', padding:'9px 12px', background:'none', border:'none', borderBottom:'1px solid #F5F5F5', cursor:'pointer', fontSize:13, fontFamily:'Tajawal, sans-serif', color:'#1A1C1C' }}>
                      {productName(p)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Author name */}
        <div style={{ marginBottom:14 }}>
          <label style={labelStyle}>{t('reviews.authorNameLabel')}</label>
          <input value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder={t('reviews.authorNamePlaceholder')} maxLength={100} style={inputStyle} />
        </div>

        {/* Rating */}
        <div style={{ marginBottom:14 }}>
          <label style={labelStyle}>{t('reviews.rating')}</label>
          <div style={{ display:'flex', gap:4 }}>
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setRating(n)} style={{ background:'none', border:'none', cursor:'pointer', padding:2 }}>
                <Star size={24} style={{ color: n <= rating ? '#FF6D00' : '#E8E8E8', fill: n <= rating ? '#FF6D00' : 'none' }} />
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom:14 }}>
          <label style={labelStyle}>{t('reviews.titleLabelOptional')}</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} style={inputStyle} />
        </div>

        {/* Content */}
        <div style={{ marginBottom:18 }}>
          <label style={labelStyle}>{t('reviews.contentLabelOptional')}</label>
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} maxLength={2000} style={{ ...inputStyle, resize:'vertical' }} />
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px 0', borderRadius:10, border:'1px solid #E8E8E8', background:'#fff', color:'#454652', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t('common.cancel')}</button>
          <button onClick={submit} disabled={!canSubmit} className="hg-btn-navy" style={{ flex:1, padding:'10px 0', fontSize:13, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {submitting ? t('reviews.saving') : t('common.add')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ReviewCard({ review, lang, t, onApprove, onReject, approveLoading, rejectLoading }) {
  const isPending = review.status === 'pending'
  return (
    <div style={{ background:'#fff', borderRadius:20, padding:20, boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)', transition:'transform 0.2s' }}
      onMouseEnter={(e) => e.currentTarget.style.transform='translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform='translateY(0)'}
    >
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12, gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:38, height:38, borderRadius:12, background:'rgba(0,6,102,0.08)', color:'#000666', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, flexShrink:0, fontFamily:'Cairo, sans-serif' }}>
            {(review.user?.name || review.authorName)?.charAt(0) || '?'}
          </div>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#1A1C1C' }}>{review.user?.name || review.authorName || t('reviews.unknownAuthor')}</span>
              {!review.user && review.authorName && (
                <span style={{ fontSize:10, fontWeight:700, fontFamily:'Tajawal, sans-serif', color:'#000666', background:'rgba(0,6,102,0.08)', padding:'1px 6px', borderRadius:6 }}>{t('reviews.fromAdminBadge')}</span>
              )}
            </div>
            <StarRating rating={review.rating} />
          </div>
        </div>
        <span style={{ fontSize:11, color:'#767683', fontFamily:'Tajawal, sans-serif', whiteSpace:'nowrap' }}>
          {formatRelativeTime(review.createdAt, lang === 'ar' ? 'ar-EG' : 'en')}
        </span>
      </div>

      {/* Product */}
      {review.product && (
        <div style={{ display:'inline-block', fontSize:11, fontWeight:700, fontFamily:'Tajawal, sans-serif', color:'#000666', background:'rgba(0,6,102,0.06)', padding:'3px 10px', borderRadius:8, marginBottom:10 }}>
          {typeof review.product.name === 'object' ? (review.product.name[lang] || review.product.name.en) : review.product.name}
        </div>
      )}

      {/* Title */}
      {review.title && (
        <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#1A1C1C', marginBottom:6 }}>{review.title}</div>
      )}

      {/* Body */}
      <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#454652', lineHeight:1.7, marginBottom:16, display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
        {review.content || review.body}
      </p>

      {/* Helpful */}
      {review.helpfulCount > 0 && (
        <div style={{ fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif', marginBottom:12 }}>
          👍 {t('reviews.helpfulCount', { count: review.helpfulCount })}
        </div>
      )}

      {/* Actions (only for pending) */}
      {isPending && (
        <div style={{ display:'flex', gap:8, borderTop:'1px solid #F3F3F3', paddingTop:14 }}>
          <button onClick={onApprove} disabled={approveLoading}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', borderRadius:10, fontSize:13, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(27,109,36,0.08)', color:'#1B6D24', border:'none', cursor:'pointer', transition:'all 0.15s', opacity:approveLoading?0.6:1 }}
            onMouseEnter={(e) => e.currentTarget.style.background='rgba(27,109,36,0.16)'}
            onMouseLeave={(e) => e.currentTarget.style.background='rgba(27,109,36,0.08)'}
          >
            <CheckCircle size={14} />{t('reviews.approve')}
          </button>
          <button onClick={onReject} disabled={rejectLoading}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', borderRadius:10, fontSize:13, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(186,26,26,0.08)', color:'#BA1A1A', border:'none', cursor:'pointer', transition:'all 0.15s', opacity:rejectLoading?0.6:1 }}
            onMouseEnter={(e) => e.currentTarget.style.background='rgba(186,26,26,0.16)'}
            onMouseLeave={(e) => e.currentTarget.style.background='rgba(186,26,26,0.08)'}
          >
            <XCircle size={14} />{t('reviews.reject')}
          </button>
        </div>
      )}
    </div>
  )
}
